import { Injectable } from '@angular/core';
import { first, Observable, timeout, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { SpaceRepository } from '../state/space.repository';
import { SpaceSettings } from '../models/session';
import { ERRORS, SUCCESSES } from '../common/utils/notification-constants';
import { ToasterPopupStyle } from '../ui/notification-toaster/custom-notification-toastr/custom-notification-toastr.component';
import { IconBackground } from '../ui/notification-toaster/icon-message-toaster-element/icon-message-toaster-element.component';
import { SpacesService } from './spaces.service';
import { NotificationToasterService } from './notification-toaster.service';
import { TelemetryService } from './telemetry.service';
import {
  NotificationIconConfig,
  NotificationIconTypes,
  NotificationType,
  V2NotificationDataBuilder,
} from './notification-toaster.service';

const DEBOUNCE_MS = 5000;

@Injectable({
  providedIn: 'root',
})
export class SpaceSettingsUpdateService {
  constructor(
    private readonly spaceRepo: SpaceRepository,
    private readonly spacesService: SpacesService,
    private readonly notificationToasterService: NotificationToasterService,
    private readonly translateService: TranslateService,
    private readonly telemetryService: TelemetryService,
  ) {}

  /**
   * Shows the standard "Permissions updated" success notification.
   * Dismisses any existing one first so multiple rapid successes (e.g. visibility + waiting room) show as a single toast.
   */
  showPermissionsUpdatedSuccess(): void {
    this.notificationToasterService.dismissNotificationsByCode([SUCCESSES.PERMISSIONS_UPDATED]);
    const notificationTitle = this.translateService.instant('Permissions updated');
    const notificationIcon: NotificationIconConfig = {
      assetName: 'check',
      type: NotificationIconTypes.MAT_ICON,
      size: 18,
      outlined: true,
      background: IconBackground.SUCCESS,
    };
    const notificationData = new V2NotificationDataBuilder(SUCCESSES.PERMISSIONS_UPDATED)
      .type(NotificationType.SUCCESS)
      .style(ToasterPopupStyle.SUCCESS)
      .title(notificationTitle)
      .icon(notificationIcon)
      .dismissable(false)
      .priority(560)
      .timeOut(5)
      .onActivateTick(true)
      .showProgressBar(true)
      .build();
    this.notificationToasterService.showNotification(notificationData);
  }

  /**
   * Updates active space settings via API, then on success updates the repo, shows "Permissions updated", and calls onSuccess.
   * On error, logs to telemetry and shows a failure notification.
   * No-op if there is no active space.
   */
  updateSettingsAndNotify(
    settingsPatch: Partial<SpaceSettings>,
    onSuccess?: () => void,
    catchErrorFn?: (err: unknown) => Observable<never>,
  ): void {
    const spaceId = this.spaceRepo.activeSpace?._id;
    if (!spaceId) {
      return;
    }
    const currentSettings = this.spaceRepo.activeSpace?.settings;

    this.spacesService
      .updateSettings(spaceId, settingsPatch as SpaceSettings, true)
      .pipe(
        timeout(DEBOUNCE_MS),
        first(),
        catchError((err) => catchErrorFn?.(err) ?? throwError(() => err)),
      )
      .subscribe({
        next: () => {
          const newSettings = this.mergeSpaceSettings(currentSettings, settingsPatch);
          this.spaceRepo.updateSpace(spaceId, { settings: newSettings });
          this.showPermissionsUpdatedSuccess();
          onSuccess?.();
        },
        error: (err) => this.handleError(spaceId, err, 'space_settings_update_failed'),
      });
  }

  private handleError(spaceId: string, err: unknown, eventName: string): void {
    this.telemetryService.errorEvent(eventName, {
      spaceId,
      message: (err as Error)?.message ?? 'Unknown error',
      name: (err as Error)?.name,
    });
    this.notificationToasterService.showDefaultErrorNotificationV2(
      this.translateService.instant('Unable to update settings'),
      this.translateService.instant('Please try again'),
      ERRORS.SPACE_UPDATE_FAILED,
      undefined,
      undefined,
      5,
    );
  }

  private mergeSpaceSettings(
    current: SpaceSettings | undefined,
    patch: Partial<SpaceSettings>,
  ): SpaceSettings {
    if (!current) {
      return patch as SpaceSettings;
    }
    const definedPatch = Object.fromEntries(
      Object.entries(patch).filter(([_, val]) => val != null),
    ) as Partial<SpaceSettings>;
    return { ...current, ...definedPatch };
  }
}
